import {inject, Injectable, Injector} from '@angular/core';
import {HttpErrorResponse} from "@angular/common/http";
import {NotificationService} from "../notification/notification.service";
import {AccStateEnum} from "./acc-state.enum";
import {BehaviorSubject, distinctUntilChanged, Observable, of, Subject} from "rxjs";
import {OrgService} from "../org.service";
import {filter, take, timeout} from "rxjs/operators";
import {isDefined, isOrg, isUuid} from "../../libs/utils/epic.utils";
import {OrgDto} from "../../repositories/dto/org.dto";
import {LoggingService} from "../logger/logging.service";
import {SettingsService} from "../settings.service";
import {SettingKey} from "../../base/setting-key.enum";
import {Additionals} from "../../repositories/item/additionals";
import {UserService} from "../user.service";
import {InfoDto} from "../../repositories/dto/info.dto";
import {InfoService} from "../info.service";
import {UserDto} from "../../repositories/dto/user.dto";
import {UrlService} from "../url.service";
import {SettingDto} from "../../repositories/dto/setting.dto";

@Injectable({providedIn: 'root'})
export class StateService {

  private readonly orgService: OrgService = inject(OrgService);
  private readonly log: LoggingService = inject(LoggingService);
  private readonly userService: UserService = inject(UserService);
  private readonly settingsService: SettingsService = inject(SettingsService);
  private readonly infoService: InfoService = inject(InfoService);
  private readonly urlService: UrlService = inject(UrlService);
  private readonly injector: Injector = inject(Injector);

  private state: BehaviorSubject<AccStateEnum> = new BehaviorSubject<AccStateEnum>(AccStateEnum.USER);
  private currentOrg: BehaviorSubject<OrgDto | undefined> = new BehaviorSubject<OrgDto | undefined>(undefined);
  private clientNotification: Subject<string> = new Subject<string>();
  private info: BehaviorSubject<InfoDto | undefined> = new BehaviorSubject<InfoDto | undefined>(undefined);
  private readonly ready: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor() {
    this.infoService.getInfo().subscribe((i) => this.info.next(i));
    this.currentOrg.subscribe((org) => this.orgService.setCurrentOrg(org));

    this.settingsService.getUserSettings().subscribe((settings) => {
      const lastState: SettingDto = settings.filter((s) => s.key === SettingKey[SettingKey.USER_LAST_STATE])[0];
      const lastOrg: SettingDto = settings.filter((s) => s.key === SettingKey[SettingKey.USER_LAST_ORG])[0];
      const switchOrg: string | undefined = this.urlService.getQueryParam('switchOrg');
      if (isUuid(switchOrg)) {
        this.log.debug('Switch to Org with ID "{}"', [switchOrg]);
        this.useOrg(switchOrg).then(() => this.ready.next(true));
      } else if (isDefined(lastState) && isDefined(lastOrg) && this.isUserState()
        && lastState.value === AccStateEnum[AccStateEnum.ORG] && lastOrg.value && isUuid(lastOrg.value)) {
        this.useOrg(lastOrg.value).then(() => this.ready.next(true));
      } else {
        this.ready.next(true);
      }
    });

    this.orgService.onOrgChanged()
      .pipe(filter((id) => isUuid(id) && id === this.currentOrg.value?.id))
      .subscribe(() => this.reloadOrg());

    this.getClientNotification()
      .pipe(filter((n) => n === 'ORG_FEATURE_CHANGED'))
      .subscribe(() => this.reloadOrg());
  }

  /**
   * Emits once the first choice of organisation is settled: last used org, `?switchOrg=` or none. Pages read their
   * permissions in the constructor - opened before this point, they built their forms with the permissions of no org.
   * At the latest after 10 s: the user settings never report an error, a failed request would otherwise hold every page.
   */
  public whenReady(): Observable<boolean> {
    return this.ready.asObservable().pipe(filter((r) => r), take(1), timeout({first: 10000, with: () => of(true)}));
  }

  public getInfo(): Observable<InfoDto> {
    return this.info.asObservable().pipe(filter((i) => isDefined(i)));
  }

  public getState(): Observable<AccStateEnum> {
    return this.state.asObservable();
  }

  public getStateDistinct(): Observable<AccStateEnum> {
    return this.getState().pipe(
      filter((s) => isDefined(s)),
      distinctUntilChanged()
    );
  }

  public getOrg(): Observable<OrgDto | undefined> {
    return this.currentOrg.asObservable();
  }

  public getOrgDistinct(): Observable<OrgDto> {
    return this.getOrg().pipe(
      filter((o) => isDefined(o)),
      filter((o) => isOrg(o)),
      distinctUntilChanged((prev, curr) => prev!.id === curr!.id));
  }

  public isUserState(): boolean {
    return this.state.value === AccStateEnum.USER;
  }

  public isOrgState(): boolean {
    return this.state.value === AccStateEnum.ORG;
  }

  public useUser(): Promise<boolean> {
    return new Promise((resolve) => {
      this.currentOrg.next(undefined);
      this.updateState(AccStateEnum.USER);
      this.settingsService.updateUserSetting(SettingKey.USER_LAST_ORG, '').then();
      resolve(true);
    });
  }

  public reloadOrg(): void {
    if (this.currentOrg.value && isUuid(this.currentOrg.value.id)) {
      this.log.debug('Reload Org "{}" with ID "{}"', [this.currentOrg.value.name, this.currentOrg.value.id]);
      this.useOrg(this.currentOrg.value.id).then();
    }
  }

  public useOrg(id: string): Promise<boolean> {
    return new Promise((resolve) => {
      const add = new Additionals({addPermissions: true, addSettings: true, addFeature: true});
      this.orgService.getById(id, add)
        .pipe(filter((org) => isDefined(org) && isUuid(org.id)))
        .subscribe({
          next: (org) => {
            this.log.debug('Use Org "{}" with ID "{}"', [org.name, org.id]);
            this.currentOrg.next(org);
            this.updateState(AccStateEnum.ORG);
            this.settingsService.updateUserSetting(SettingKey.USER_LAST_ORG, id).then();
            resolve(true);
          },
          // Never reject: every caller only uses '.then()', so a rejection would end up as an unhandled promise
          // rejection and the user would get no feedback at all
          error: (ex: HttpErrorResponse) => this.handleUseOrgError(id, ex).then(() => resolve(false))
        });
    });
  }

  public getUser(): Observable<UserDto> {
    return this.userService.getUser();
  }

  private handleUseOrgError(id: string, ex: HttpErrorResponse): Promise<boolean> {
    this.log.warn('Org with ID "{}" cannot be used, status "{}"', [id, ex.status]);
    /*
      🔴 Nicht 'user.permission.missing': Ein 403 heißt hier auch „Vertrag beendet, Zugang gesperrt" (§ 10 Abs. 3, Stufe 2), und die Rechte
      des Nutzers sind dann unversehrt. Der Rumpf des 403 ist mit Absicht leer — ein Fehler, der sich erklärt, verrät einem Fremden, welche
      Organisationen es gibt —, hier lässt sich der Grund also gar nicht unterscheiden. Deshalb eine Meldung, die in beiden Fällen stimmt;
      den Grund nennt die Organisationsliste, auf der man gleich darauf landet.
    */
    this.addErrorMessage(ex.status === 403 ? 'org.use.forbidden' : 'exception.unknown');
    if (ex.status >= 400 && ex.status < 500) {
      // The org stays unusable for this user. Switching back clears USER_LAST_ORG, otherwise the stored org
      // retriggers the same failure on every start (see constructor)
      return this.useUser();
    }
    return Promise.resolve(false);
  }

  // NotificationService depends on StateService (see AbstractService), so it must be resolved lazily
  private addErrorMessage(message: string): void {
    this.injector.get(NotificationService).addErrorMessage(message);
  }

  private updateState(state: AccStateEnum): void {
    this.log.debug('State changed "{}"', [AccStateEnum[state]]);
    this.state.next(state);
    this.settingsService.updateUserSetting(SettingKey.USER_LAST_STATE, AccStateEnum[state]).then();
  }

  // ##########################################################################
  // #### Client notifications
  // ##########################################################################

  public getClientNotification(): Observable<string> {
    return this.clientNotification.asObservable();
  }

  // see stateInterceptor
  public setClientNotifications(notification: string[]): void {
    notification.forEach((n) => this.setClientNotification(n));
  }

  private setClientNotification(notification: string): void {
    this.clientNotification.next(notification);
  }
}
